(() => {
  'use strict';
  const CORE_HANDLES = Object.freeze(['nuit-paisible', 'digestion', 'relax', 'energy']);
  const ELIGIBLE_HANDLES = new Set([...CORE_HANDLES, 'detox', 'immunite']);
  const VERIFIED_PRICE_HANDLES = new Set(ELIGIBLE_HANDLES);

  function presetHandles(size, handle) {
    if (![2, 3, 4].includes(size) || !ELIGIBLE_HANDLES.has(handle)) return [];
    return size < 4 ? Array(size).fill(handle) : [handle, ...CORE_HANDLES].filter((item, index, all) => all.indexOf(item) === index).slice(0, 4);
  }

  function createPlan(choices, size, variants, uniqueChoices) {
    if (![2, 3, 4].includes(size) || choices.length !== size) return { valid: false, message: 'Choisissez un format de 2, 3 ou 4 flacons.' };
    const byId = new Map(variants.filter((variant) => ELIGIBLE_HANDLES.has(variant.handle)).map((variant) => [String(variant.id), variant]));
    const chosen = choices.map((id) => byId.get(String(id)));
    const missing = chosen.filter((variant) => !variant).length;
    if (missing) return { valid: false, missing, message: `Il reste ${missing} flacon${missing > 1 ? 's' : ''} à choisir.` };
    if (chosen.some((variant) => !variant.available)) return { valid: false, message: 'Une formule est indisponible. Choisissez une autre formule.' };
    if (uniqueChoices && new Set(chosen.map((variant) => String(variant.productId))).size !== size) return { valid: false, message: 'Choisissez une formule différente pour chaque flacon.' };
    const quantities = new Map();
    for (const variant of chosen) quantities.set(String(variant.id), (quantities.get(String(variant.id)) || 0) + 1);
    for (const [id, quantity] of quantities) {
      const variant = byId.get(id);
      const minimum = Number(variant.min) || 1;
      const step = Number(variant.step) || 1;
      if (quantity < minimum || (quantity - minimum) % step !== 0 || (variant.max != null && quantity > Number(variant.max))) return { valid: false, message: 'Cette quantité ne correspond pas au format disponible. Consultez la fiche de la formule.' };
    }
    return { valid: true, chosen, items: [...quantities].map(([id, quantity]) => ({ id: Number(id), quantity })), subtotal: chosen.reduce((total, variant) => total + Number(variant.price), 0) };
  }

  function cartQuantities(cart) {
    const quantities = new Map();
    for (const item of cart.items || []) {
      const id = String(item.variant_id || item.id);
      quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity));
    }
    return quantities;
  }

  function reconcileAddition(before, after, items) {
    const prior = cartQuantities(before);
    const current = cartQuantities(after);
    return items.every((item) => (current.get(String(item.id)) || 0) - (prior.get(String(item.id)) || 0) === item.quantity);
  }

  function itemsWithGift(items, size, gift, before) {
    const result = items.map((item) => ({ ...item }));
    const giftAlreadyPresent = gift && before.items.some((item) => String(item.product_id) === String(gift.productId) || String(item.variant_id || item.id) === String(gift.id));
    if (size === 4 && gift?.available && !giftAlreadyPresent) {
      const giftItem = { id: Number(gift.id), quantity: 1 };
      // Preserve the marker generated by the active AppHero campaign.
      if (gift.properties && typeof gift.properties === 'object' && !Array.isArray(gift.properties)) giftItem.properties = { ...gift.properties };
      result.push(giftItem);
    }
    return result;
  }

  function giftIsFree(cart, gift) {
    const lines = cart.items.filter((item) => String(item.product_id) === String(gift.productId) || String(item.variant_id || item.id) === String(gift.id));
    return lines.length > 0 && lines.every((item) => item.final_line_price === 0);
  }

  function verifiedPrice(plan, currency) {
    if (!plan.valid || currency !== 'EUR' || !plan.chosen.every(v => VERIFIED_PRICE_HANDLES.has(v.handle) && Number(v.price) === 1990)) return null;
    // Representative Shopify checks on 2026-09-10 cover every quantity partition
    // and the six-formula scope at 19.90 EUR. Shopify rounds each discounted unit:
    // Duo 17.91, Trio 16.32, four bottles 14.93 EUR (not the rounded subtotal).
    const size = plan.chosen.length;
    return ({ 2: 3582, 3: 4896, 4: 5972 })[size] ?? null;
  }

  function createPreset(size, handle, variants, uniqueChoices = false) {
    if (![2, 3, 4].includes(size) || !ELIGIBLE_HANDLES.has(handle)) return { accepted: false, reason: 'invalid-preset' };
    const handles = presetHandles(size, handle);
    const chosen = handles.map(item => variants.find(v => v.handle === item && v.available) || variants.find(v => v.handle === item));
    const choices = chosen.map(variant => variant ? String(variant.id) : '');
    const plan = createPlan(choices, size, variants, uniqueChoices);
    const reason = chosen.some(variant => !variant?.available) ? 'unavailable' : plan.valid ? null : 'invalid-quantity';
    return { accepted: true, size, handle, choices, plan, reason };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createPlan, createPreset, presetHandles, cartQuantities, reconcileAddition, itemsWithGift, giftIsFree, verifiedPrice };
  if (typeof document === 'undefined') return;
  let submissionInFlight = false;

  async function request(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try { return await fetch(url, { ...options, signal: controller.signal }); }
    finally { clearTimeout(timer); }
  }

  async function getCart(url) {
    const response = await request(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('cart-read');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('cart-read');
    return cart;
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-dizane-bundles]').forEach((section) => {
      if (section.dataset.bundleReady) return;
      let config;
      try { config = JSON.parse(section.querySelector('[data-bundle-config]').textContent); } catch { return; }
      const form = section.querySelector('[data-bundle-form]');
      const slots = [...section.querySelectorAll('[data-bundle-slot]')];
      const selectors = slots.map((slot) => slot.querySelector('[data-bundle-choice]'));
      const submit = section.querySelector('[data-bundle-submit]');
      const progress = section.querySelector('[data-bundle-progress]');
      const summary = section.querySelector('[data-bundle-summary]');
      const subtotal = section.querySelector('[data-bundle-subtotal]');
      const totalLabel = section.querySelector('[data-bundle-total-label]');
      const discount = section.querySelector('[data-bundle-discount]');
      const reference = section.querySelector('[data-bundle-reference]');
      const shipping = section.querySelector('[data-bundle-shipping]');
      const priceNote = section.querySelector('[data-bundle-price-note]');
      const summaryHeading = section.querySelector('[data-bundle-summary-heading]');
      const message = section.querySelector('[data-bundle-message]');
      const review = section.querySelector('[data-bundle-review]');
      let locked = false;
      const variants = config.variants.filter((variant) => ELIGIBLE_HANDLES.has(variant.handle)).sort((a, b) => Number(b.handle === config.currentProductHandle) - Number(a.handle === config.currentProductHandle));
      const initialPresetHandle = ['night-trio', 'same-trio'].includes(section.dataset.preset)
        ? section.dataset.presetHandle || (section.dataset.preset === 'night-trio' ? 'nuit-paisible' : '') : '';
      let presetHandle = ELIGIBLE_HANDLES.has(initialPresetHandle) ? initialPresetHandle : null;
      let presetSize = presetHandle ? 3 : null;
      const initialPreset = presetHandle && createPreset(3, presetHandle, variants, config.uniqueChoices);
      if (initialPreset?.accepted) selectors.slice(0, 3).forEach((select, index) => { select.value = initialPreset.choices[index]; });
      const formatMoney = (cents) => new Intl.NumberFormat(config.locale || 'fr-FR', { style: 'currency', currency: config.currency || 'EUR' }).format(cents / 100);
      const currentSize = () => Number(section.querySelector('[data-bundle-size]:checked')?.value || 4);
      const currentChoices = () => selectors.slice(0, currentSize()).map((selector) => selector.value);
      const defaultHandles = presetHandles(4, ELIGIBLE_HANDLES.has(config.currentProductHandle) ? config.currentProductHandle : CORE_HANDLES[0]);
      const cartUrl = new URL(config.cartUrl, window.location.origin);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) cartUrl.searchParams.set('preview_theme_id', preview);
      review.href = cartUrl.href;

      function fillAvailable() {
        if (!config.prefill) return;
        // An unavailable named preset must not silently become three other formulas.
        if ([2, 3].includes(presetSize) && currentSize() === presetSize && presetHandle && !variants.some(v => v.handle === presetHandle && v.available)) return;
        const usedProducts = new Set(currentChoices().map((id) => variants.find((variant) => String(variant.id) === id)?.productId).filter(Boolean));
        selectors.slice(0, currentSize()).forEach((selector) => {
          if (selector.value) return;
          const available = defaultHandles.map(handle => variants.find(variant => variant.handle === handle && variant.available && !usedProducts.has(variant.productId))).find(Boolean);
          if (available) { selector.value = String(available.id); usedProducts.add(available.productId); }
        });
      }

      function render() {
        const size = currentSize();
        slots.forEach((slot, index) => {
          slot.hidden = index >= size;
          selectors[index].disabled = index >= size || locked;
          selectors[index].required = index < size;
        });
        const choices = currentChoices();
        const plan = createPlan(choices, size, variants, config.uniqueChoices);
        const confirmedPrice = verifiedPrice(plan, config.currency);
        const offerVerified = plan.valid && plan.chosen.every(variant => VERIFIED_PRICE_HANDLES.has(variant.handle));
        const sameFormula = plan.valid && new Set(plan.chosen.map(variant => variant.handle)).size === 1;
        const shortTitle = sameFormula ? plan.chosen[0].title.replace(/^Dizane\s+/i, '') : '';
        const requestedHandles = presetHandle && size === presetSize ? presetHandles(size, presetHandle) : [];
        const matchesPreset = Boolean(plan.valid && requestedHandles.length === size && (size < 4
          ? plan.chosen.every(variant => variant.handle === presetHandle)
          : requestedHandles.every(handle => plan.chosen.filter(variant => variant.handle === handle).length === 1)));
        const selected = choices.map(id => variants.find(variant => String(variant.id) === id));
        const requestedUnavailable = presetHandle && size === presetSize && requestedHandles.some(handle => !variants.some(variant => variant.handle === handle && variant.available));
        const reason = plan.valid ? null : requestedUnavailable || selected.some(variant => variant && !variant.available) ? 'unavailable'
          : selected.some(variant => !variant) ? 'incomplete' : 'invalid-selection';
        const composition = section.querySelector('[data-bundle-composition]');
        if (composition) {
          composition.textContent = plan.valid ? plan.items.map(item => {
            const variant = variants.find(v => Number(v.id) === item.id);
            const title = variant.title.replace(/^Dizane\s+/i, '');
            const format = variant.variantTitle && variant.variantTitle !== 'Default Title' ? ` (${variant.variantTitle})` : '';
            return `${item.quantity} × ${title}${format}`;
          }).join(' · ') : '';
        }
        const customize = section.querySelector('.de-customize');
        if (!plan.valid && customize) customize.open = true;
        summary.replaceChildren();
        choices.forEach((id, index) => {
          const variant = variants.find((item) => String(item.id) === id);
          const row = document.createElement('li');
          const label = document.createElement('span');
          label.textContent = variant ? variant.title : `Flacon ${index + 1} · à choisir`;
          row.append(label);
          if (variant) { const price = document.createElement('strong'); price.textContent = formatMoney(confirmedPrice != null ? confirmedPrice / size : variant.price); row.append(price); }
          summary.append(row);
          const media = slots[index].querySelector('[data-slot-image]');
          media.replaceChildren();
          if (variant?.image) { const image = document.createElement('img'); image.src = variant.image; image.alt = ''; image.width = 60; image.height = 60; media.append(image); }
        });
        if (size === 4 && config.gift?.available) {
          const row = document.createElement('li');
          const title = document.createElement('span');
          title.textContent = config.gift.title;
          const price = document.createElement('strong');
          price.textContent = 'Offert';
          row.append(title, price);
          row.className = 'dz-bundles__gift';
          summary.append(row);
        }
        subtotal.textContent = plan.valid ? formatMoney(confirmedPrice ?? plan.subtotal) : '—';
        totalLabel.textContent = confirmedPrice != null ? `${size} flacons` : 'Avant remise';
        if (reference) reference.textContent = confirmedPrice != null ? `Au lieu de ${formatMoney(plan.subtotal)} à l’unité` : '';
        discount.textContent = plan.valid ? (confirmedPrice != null ? `${formatMoney(plan.subtotal - confirmedPrice)} économisés · −${{ 2: 10, 3: 18, 4: 25 }[size]} %` : offerVerified ? `−${{ 2: 10, 3: 18, 4: 25 }[size]} % au panier` : 'Offre confirmée au panier') : '';
        const perGlass = section.querySelector('[data-bundle-per-glass]');
        if (perGlass) perGlass.textContent = confirmedPrice != null ? `${size * 25} verres à 10 ml · ${formatMoney(confirmedPrice / (size * 25))} / verre` : `${size * 25} verres à 10 ml`;
        const giftNote = section.querySelector('[data-bundle-gift]');
        if (giftNote) giftNote.hidden = !plan.valid || size !== 4 || !config.gift?.available;
        if (shipping) {
          shipping.hidden = !plan.valid;
          const delivery = config.delivery;
          shipping.textContent = delivery ? delivery.message : '';
        }
        priceNote.textContent = confirmedPrice != null ? 'Livraison selon la destination : consultez les pays et tarifs ci-dessous.' : 'Prix et avantages confirmés au panier.';
        summaryHeading.textContent = size === 4
          ? sameFormula ? `Vos 4 ${shortTitle}.` : plan.valid && new Set(plan.chosen.map(variant => variant.handle)).size === 4 ? 'Vos quatre formules.' : 'Votre coffret Dizane.'
          : size === 3 && sameFormula ? `Votre trio ${shortTitle}.` : `Votre ${size === 2 ? 'duo' : 'trio'} Dizane.`;
        progress.textContent = plan.valid ? `${size} flacons choisis.` : plan.message;
        section.dataset.planValid = String(plan.valid);
        section.dataset.priceVerified = String(confirmedPrice != null);
        section.dataset.confirmedPrice = confirmedPrice == null ? '' : String(confirmedPrice);
        submit.disabled = !plan.valid || locked;
        if (!locked) submit.textContent = plan.valid ? `${size === 3 && sameFormula ? `Ajouter 3 ${shortTitle}` : `Ajouter mes ${size} flacons`}${confirmedPrice != null ? ` — ${formatMoney(confirmedPrice)}` : ' au panier →'}` : 'Complétez votre sélection';
        section.dispatchEvent(new CustomEvent('dizane:bundle:change', { bubbles: true, detail: { size, valid: plan.valid, locked, confirmedPrice, presetHandle, presetSize, matchesPreset, reason } }));
      }

      function showReview(text) {
        locked = true;
        render();
        submit.textContent = 'Vérifiez votre panier avant un nouvel ajout';
        message.textContent = text;
        message.hidden = false;
        review.hidden = false;
        review.focus();
      }

      section.addEventListener('dizane:bundle:preset', event => {
        if (event.target !== section) return;
        const request = event.detail;
        if (!request || typeof request !== 'object' || Object.isFrozen(request)) { event.preventDefault(); return; }
        request.accepted = false;
        request.reason = locked || submissionInFlight ? 'busy' : 'invalid-preset';
        if (locked || submissionInFlight) { event.preventDefault(); return; }
        const next = createPreset(Number(request.size), request.handle, variants, config.uniqueChoices);
        const radio = section.querySelector(`[data-bundle-size][value="${next.size}"]`);
        if (!next.accepted || !radio) { event.preventDefault(); return; }
        presetHandle = next.handle;
        presetSize = next.size;
        selectors.forEach((selector, index) => { selector.value = next.choices[index] || ''; });
        radio.checked = true;
        message.hidden = true;
        request.accepted = true;
        request.reason = next.reason;
        render();
        if (next.reason === 'unavailable') {
          message.textContent = 'Une formule de cette sélection est indisponible. Choisissez vos flacons ci-dessous.';
          message.hidden = false;
        }
      });

      selectors.forEach((selector) => selector.addEventListener('change', () => { message.hidden = true; render(); }));
      section.querySelectorAll('[data-bundle-size]').forEach((radio) => radio.addEventListener('change', () => { if (!locked) { fillAvailable(); render(); } }));
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (locked || submissionInFlight) return;
        const submittedSize = currentSize();
        const plan = createPlan(currentChoices(), submittedSize, variants, config.uniqueChoices);
        if (!plan.valid) { render(); return; }
        locked = true;
        submissionInFlight = true;
        section.querySelectorAll('[data-bundle-size]').forEach((radio) => { radio.disabled = true; });
        render();
        submit.textContent = 'Ajout de votre sélection…';
        message.hidden = true;
        let before;
        try { before = await getCart(config.cartJsonUrl); }
        catch {
          locked = false;
          submissionInFlight = false;
          section.querySelectorAll('[data-bundle-size]').forEach((radio) => { radio.disabled = false; });
          render();
          message.textContent = 'Votre panier n’a pas pu être chargé. Aucun ajout n’a été envoyé. Réessayez dans un instant.';
          message.hidden = false;
          return;
        }
        const items = itemsWithGift(plan.items, submittedSize, config.gift, before);
        let responseOk = false;
        try {
          const response = await request(config.addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
          responseOk = response.ok;
        } catch { /* Never repeat an uncertain cart mutation automatically. */ }
        let after;
        try { after = await getCart(config.cartJsonUrl); } catch { /* The customer must review the native cart. */ }
        submissionInFlight = false;
        if (after && reconcileAddition(before, after, items)) {
          if (submittedSize === 4 && config.gift?.available && !giftIsFree(after, config.gift)) {
            showReview('Vos flacons ont été ajoutés, mais la gratuité du cadeau n’a pas pu être confirmée. Vérifiez le prix du cadeau dans votre panier avant de poursuivre.');
            return;
          }
          window.location.assign(cartUrl.href);
          return;
        }
        showReview(responseOk
          ? 'Votre sélection a été envoyée, mais son ajout complet n’a pas pu être confirmé. Vérifiez les flacons et les quantités dans votre panier avant de poursuivre.'
          : 'Votre sélection n’a pas pu être ajoutée entièrement ou confirmée. Certains flacons peuvent déjà être dans votre panier. Vérifiez-le avant de poursuivre.');
      });
      fillAvailable();
      section.dataset.bundleReady = 'true';
      render();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();